import { LocationAndEtaDetailType, LocationObjectType } from "../base/types";
import Timeout = NodeJS.Timeout;

export type LocationSubscribeFunctionType = (location: LocationAndEtaDetailType, timestamp: number) => void;

export type ReloadLocationFunctionType = () => (LocationAndEtaDetailType | Promise<LocationAndEtaDetailType>);

export type ConfigType = {
  FORCE_RELOAD_TIME_THRESHOLD: number,
  MAX_NOTIFY_TIME_THRESHOLD: number,
  MIN_NOTIFY_TIME_THRESHOLD: number
};

export type PartialConfigType = {
  FORCE_RELOAD_TIME_THRESHOLD?: number,
  MAX_NOTIFY_TIME_THRESHOLD?: number,
  MIN_NOTIFY_TIME_THRESHOLD?: number
};

const defaultConfig: ConfigType = {
  FORCE_RELOAD_TIME_THRESHOLD: 3 * 1000, // 3 seconds
  MAX_NOTIFY_TIME_THRESHOLD: 10 * 1000, // 10 seconds
  MIN_NOTIFY_TIME_THRESHOLD: 1 * 1000, // 1 seconds
};

const initialLocation: LocationObjectType = {
  latitude: 41.0568431,
  longitude: 28.9983115,
}

const typeCheckBaseMessageHelper = (type: String) =>
  `location should be of type "{ longitude: number, latitude: number }" but is of type "${type}"`;

const typeCheckEtaMessageHelper = (type: String) =>
  `eta should be of type "{ distance: number, time: number }" but is of type "${type}"`;

class LocationTracker {
  config: ConfigType;
  location: LocationAndEtaDetailType;
  lastNotifiedLocation: LocationAndEtaDetailType;
  timestamp: number;
  notifyTimestamp: number;
  _subscribers: Set<LocationSubscribeFunctionType>;
  _fastSubscribers: Set<LocationSubscribeFunctionType>;
  _intervalId?: Timeout;

  constructor() {
    this.lastNotifiedLocation = this.location = { location: initialLocation };
    this.notifyTimestamp = this.timestamp = Date.now() - 24 * 3600 * 1000; // 1 days ago :P
    this._subscribers = new Set<LocationSubscribeFunctionType>();
    this._fastSubscribers = new Set<LocationSubscribeFunctionType>();
    this.config = defaultConfig;
  }

  setConfig = (nextConfig: PartialConfigType) => {
    Object.keys(defaultConfig).forEach((key) => {
      this.config[key] = nextConfig[key] || defaultConfig[key];
    })
  }

  reloadLocationFunction: ReloadLocationFunctionType = () => Promise.resolve(this.location);

  setReloadLocationFunction = (reloadLocationFunction: ReloadLocationFunctionType) => {
    this.reloadLocationFunction = reloadLocationFunction;
  };

  typeCheckLocation = ({ location }: LocationAndEtaDetailType) => {
    if (typeof location !== 'object') {
      this.throw(typeCheckBaseMessageHelper(typeof location));
    }
    if (!location) {
      this.throw(typeCheckBaseMessageHelper('null'));
    }
    if (typeof location.longitude !== 'number' || typeof location.latitude !== 'number') {
      this.throw(typeCheckBaseMessageHelper(`{ ${
        Object.entries(location)
          .map(([key, value]) => `${key}: ${value}`)
          .join(', ')
      } }`))
    }
  }

  typeCheckEtaEtc = ({ eta, accuracy, bearing, speed, altitude }: LocationAndEtaDetailType) => {
    if (eta) {
      if (typeof eta !== 'object') {
        this.throw(typeCheckEtaMessageHelper(typeof eta));
      }
      if (typeof eta.distance !== 'number' || typeof eta.time !== 'number') {
        this.throw(typeCheckEtaMessageHelper(`{ ${
          Object.entries(eta)
            .map(([key, value]) => `${key}: ${value}`)
            .join(', ')
        } }`))
      }
      if (!accuracy) {
        this.throw(`accuracy is required when eta is given but accuracy is ${accuracy}`);
      }
      if (!bearing) {
        this.throw(`bearing is required when eta is given but bearing is ${bearing}`);
      }
      if (!speed) {
        this.throw(`speed is required when eta is given but speed is ${speed}`);
      }
      if (!altitude) {
        this.throw(`altitude is required when eta is given but altitude is ${altitude}`);
      }
    }
  }

  handleLocationChange = (newLocation: LocationAndEtaDetailType) => {
    const newTimestamp = Date.now();
    this.typeCheckLocation(newLocation);
    this.typeCheckEtaEtc(newLocation);
    this.location = {
      ...newLocation,
      location: {
        latitude: newLocation.location.latitude,
        longitude: newLocation.location.longitude
      }
    };
    this.timestamp = newTimestamp;
    this.notifyFastSubscribers();
    if (
      this.timestamp - this.notifyTimestamp > this.config.MIN_NOTIFY_TIME_THRESHOLD && (
        this.location.location.latitude !== this.lastNotifiedLocation.location.latitude ||
        this.location.location.longitude !== this.lastNotifiedLocation.location.longitude ||
        this.timestamp - this.notifyTimestamp >= this.config.MAX_NOTIFY_TIME_THRESHOLD
      )
    ) {
      // NOTIFY SUBSCRIBERS
      this.notifySubscribers();
    }
  }

  forceReloadLocation = async() => {
    const beforeTime = Date.now();
    const newLocation = await this.reloadLocationFunction();
    if (this.timestamp <= beforeTime) {
      this.handleLocationChange(newLocation);
    }
  }

  tick = () => {
    const now = Date.now();
    if (now - this.timestamp > this.config.FORCE_RELOAD_TIME_THRESHOLD) {
      this.forceReloadLocation()
        .catch((e) => {
          this.throw(`${e.message} - ${JSON.stringify(e)}`);
        });
    }
  }

  startTracking = () => {
    if (!this._intervalId) {
      this._intervalId = setInterval(this.tick, 1000);
    }
  }

  stopTracking = () => {
    if (this._intervalId) {
      clearInterval(this._intervalId);
      this._intervalId = undefined;
    }
  }

  notifySubscribers = () => {
    this.notifyTimestamp = this.timestamp;
    this.lastNotifiedLocation = this.location;
    this._subscribers.forEach((subscriber: LocationSubscribeFunctionType) => {
      subscriber(this.lastNotifiedLocation, this.notifyTimestamp);
    });
  }

  notifyFastSubscribers = () => {
    this._fastSubscribers.forEach((subscriber: LocationSubscribeFunctionType) => {
      subscriber(this.location, this.timestamp);
    });
  }

  subscribe = (subscriber: LocationSubscribeFunctionType) => {
    if(this._subscribers.has(subscriber)) this.throw("Already subscribed");
    this._subscribers.add(subscriber);
    const unsubscribe = () => {
      if (!this._subscribers.has(subscriber)) this.throw("Already unsubscribed");
      this._subscribers.delete(subscriber);
    }
    return unsubscribe;
  }

  fastSubscribe = (subscriber: LocationSubscribeFunctionType) => {
    if(this._fastSubscribers.has(subscriber)) this.throw("Already subscribed");
    this._fastSubscribers.add(subscriber);
    const unsubscribe = () => {
      if (!this._fastSubscribers.has(subscriber)) this.throw("Already unsubscribed");
      this._fastSubscribers.delete(subscriber);
    }
    return unsubscribe;
  }

  throw = (message: string) => {
    throw new Error(`ERROR IN LOCATION TRACKER: ${message}`);
  }
}

const locationTracker = new LocationTracker();

export default locationTracker;

export const handleLocationChange = locationTracker.handleLocationChange;

export const setConfig = locationTracker.setConfig;

export const setReloadLocationFunction = locationTracker.setReloadLocationFunction;

export const startTracking = locationTracker.startTracking;

export const stopTracking = locationTracker.stopTracking;

export const subscribe = locationTracker.subscribe;

export const fastSubscribe = locationTracker.fastSubscribe;
